/*
 * File: EmailOptOutConfirmationModal.jsx
 * Project: our-wave-stories-app
 *
 * Created by Brendan Michaelsen on February 4, 2022 at 4:30 PM
 * Copyright © 2023 Our Wave, Inc. All rights reserved.
 *
 * Last Modified: May 7, 2025 at 12:06 PM
 * Modified By: Brendan Michaelsen
 */

/**
 * Imports
 */

// Modules
import React, { useEffect } from 'react';
import PropTypes from 'prop-types';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Trans, useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';

// Components
import { Button } from '../Button';
import { Modal } from '../Modal';
import { Typography } from '../Typography';

// Constants
import { AVAILABLE_I18N_NAMESPACES } from '../../../constants/base.constants';

// Styles
import * as S from './EmailOptOutConfirmationModal.styles';


/**
 * Component
 */

export const EmailOptOutConfirmationModal = ({
	className,
	isOpen,
	handleCancel,
	handleConfirm
}) => {

	// Get translation hook
	const [t] = useTranslation(AVAILABLE_I18N_NAMESPACES.CLIENT);

	// Get current partner from hook
	const partner = useSelector((state) => state.partner.value);

	// Handle actions on component load
	useEffect(() => {}, []);

	// Render component
	return (
		<Modal className={className} isOpen={isOpen} clickOutsideClose={false} showClose={false} variant="medium" useWrapper>
			<S.ModalInner>

				{/* Header */}
				<S.Header>

					{/* Icon */}
					<S.IconContainer>
						<FontAwesomeIcon icon={['far', 'info-circle']} />
					</S.IconContainer>

					{/* Prompt */}
					<Typography tag="h4">
						<Trans t={t} i18nKey="modals.modalInquiry">
							Just Checking...
						</Trans>
					</Typography>

				</S.Header>

				{/* Content */}
				<S.Content>
					<Typography tag="p" weight="bold" style={{ margin: '0px 0px 8px' }}>
						<Trans t={t} i18nKey="emailOptOutConfirmationModal.title">
							You have chosen to not receive emails from
							{' '}
							{{ partnerName: partner.name }}
							.
						</Trans>
					</Typography>
					<Typography tag="p">
						<Trans t={t} i18nKey="emailOptOutConfirmationModal.description">
							Without emails, you won&apos;t receive helpful resources, secure story removal links, or responses to information if you requested it from our team. Your safety is our priority. If you want to receive these emails, click
							{' '}
							<Typography weight="bold">Allow Emails</Typography>
							.
						</Trans>
					</Typography>
				</S.Content>

				{/* Actions */}
				<S.ActionContainer>
					<Button variant="outline" onClick={handleCancel}>
						<Trans t={t} i18nKey="emailOptOutConfirmationModal.allowButton">
							Allow emails
						</Trans>
					</Button>
					<Button variant="solid" onClick={handleConfirm}>
						<Trans t={t} i18nKey="emailOptOutConfirmationModal.continueButton">
							Continue without emails
						</Trans>
					</Button>
				</S.ActionContainer>

			</S.ModalInner>
		</Modal>
	);
};


/**
 * Configuration
 */

EmailOptOutConfirmationModal.displayName = 'EmailOptOutConfirmationModal';
EmailOptOutConfirmationModal.propTypes = {
	className: PropTypes.string,
	isOpen: PropTypes.bool,
	handleCancel: PropTypes.func,
	handleConfirm: PropTypes.func,
};
EmailOptOutConfirmationModal.defaultProps = {
	className: null,
	isOpen: false,
	handleCancel: null,
	handleConfirm: null
};
